import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { CopyableMint } from '../common/CopyButton';
import '../../styles/compact-manual-trading.css';

const CompactManualTrading = ({ activeWallet, onTradeComplete }) => {
  const { tokens } = useAuth();
  const { showSuccess, showError, showTransaction } = useToast();
  
  const [tradeData, setTradeData] = useState({
    tokenMint: '',
    amount: '',
    tradeType: 'buy',
    slippage: 0.5
  });
  
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('input'); // input, quote, execute
  
  // New state for enhanced features
  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenInfoLoading, setTokenInfoLoading] = useState(false);
  const [calculatedAmount, setCalculatedAmount] = useState(null);
  const [tradingFees, setTradingFees] = useState(null);
  const [amountCalculating, setAmountCalculating] = useState(false);
  const [walletBalance, setWalletBalance] = useState({ sol: 0, tokens: 0 });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumback.civicstech.name.ng/api';

  // Debounce ref for token fetching
  const tokenFetchTimeoutRef = useRef(null);

  // Authenticated request helper
  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('Please log in to use trading features');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // Validate Solana address format
  const isValidSolanaAddress = (address) => {
    if (!address || address.length !== 44) return false;
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
    return base58Regex.test(address);
  };

  // Fetch token information when mint address changes
  const fetchTokenInfo = useCallback(async (mintAddress) => {
    if (!mintAddress || !isValidSolanaAddress(mintAddress)) {
      return;
    }

    setTokenInfoLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/token-info/${mintAddress}`);
      const data = await response.json();

      if (data.success) {
        setTokenInfo(data.data.token);
      } else {
        setTokenInfo(null);
      }
    } catch (error) {
      console.error('Error fetching token info:', error);
      setTokenInfo(null);
    } finally {
      setTokenInfoLoading(false);
    }
  }, [API_BASE_URL]);

  // Debounced version of fetchTokenInfo
  const debouncedFetchTokenInfo = useCallback((mintAddress) => {
    if (tokenFetchTimeoutRef.current) {
      clearTimeout(tokenFetchTimeoutRef.current);
    }

    tokenFetchTimeoutRef.current = setTimeout(() => {
      fetchTokenInfo(mintAddress);
    }, 500); // 500ms debounce
  }, [fetchTokenInfo]);

  // Calculate amount based on input
  const calculateAmount = useCallback(async (mintAddress, amount, direction) => {
    if (!mintAddress || !amount || amount <= 0) {
      setCalculatedAmount(null);
      return;
    }
    
    setAmountCalculating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/token-info/calculate-amount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tokenMint: mintAddress,
          solAmount: direction === 'sol-to-token' ? amount : undefined,
          tokenAmount: direction === 'token-to-sol' ? amount : undefined,
          direction
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setCalculatedAmount(data.data.calculation);
      } else {
        setCalculatedAmount(null);
      }
    } catch (error) {
      console.error('Error calculating amount:', error);
      setCalculatedAmount(null);
    } finally {
      setAmountCalculating(false);
    }
  }, [API_BASE_URL]);

  // Calculate trading fees
  const calculateTradingFees = useCallback(async (inputMint, outputMint, amount) => {
    if (!inputMint || !outputMint || !amount) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/token-info/trading-fees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          inputMint,
          outputMint,
          amount: parseFloat(amount)
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTradingFees(data.data.fees);
      }
    } catch (error) {
      console.error('Error calculating fees:', error);
    }
  }, [API_BASE_URL]);

  // Add token to assets
  const addTokenToAssets = async () => {
    if (!tradeData.tokenMint || !tokenInfo) return;
    
    try {
      const response = await makeAuthenticatedRequest('/token-info/add-to-assets', {
        method: 'POST',
        body: JSON.stringify({
          mintAddress: tradeData.tokenMint
        })
      });
      
      if (response.success) {
        showSuccess(`${tokenInfo.symbol} added to your assets!`);
      }
    } catch (error) {
      showError('Failed to add token to assets');
    }
  };

  // Fetch wallet balance
  const fetchWalletBalance = useCallback(async () => {
    if (!activeWallet) return;
    
    try {
      const response = await makeAuthenticatedRequest(`/wallets/${activeWallet.id}/balance`);
      if (response.success) {
        setWalletBalance({
          sol: response.data.sol || 0,
          tokens: response.data.tokens?.[tradeData.tokenMint] || 0
        });
      }
    } catch (error) {
      console.warn('Could not fetch wallet balance:', error.message);
    }
  }, [activeWallet, tradeData.tokenMint, makeAuthenticatedRequest]);

  // Set percentage amount based on wallet balance
  const setPercentageAmount = (percentage) => {
    if (!activeWallet) {
      showError('Please connect a wallet first');
      return;
    }

    let maxAmount = 0;
    if (tradeData.tradeType === 'buy') {
      // For buying, use percentage of SOL balance (minus some for fees)
      maxAmount = Math.max(0, walletBalance.sol - 0.01); // Reserve 0.01 SOL for fees
    } else {
      // For selling, use percentage of token balance
      maxAmount = walletBalance.tokens;
    }

    const amount = (maxAmount * percentage / 100).toFixed(6);
    setTradeData({ ...tradeData, amount: amount });
  };

  // Effects
  useEffect(() => {
    if (tradeData.tokenMint) {
      debouncedFetchTokenInfo(tradeData.tokenMint);
    } else {
      setTokenInfo(null);
      setCalculatedAmount(null);
      setTradingFees(null);
    }
  }, [tradeData.tokenMint, debouncedFetchTokenInfo]);

  useEffect(() => {
    if (tradeData.tokenMint && tradeData.amount) {
      const direction = tradeData.tradeType === 'buy' ? 'sol-to-token' : 'token-to-sol';
      calculateAmount(tradeData.tokenMint, parseFloat(tradeData.amount), direction);
      
      // Calculate fees
      const SOL_MINT = 'So11111111111111111111111111111111111111112';
      const inputMint = tradeData.tradeType === 'buy' ? SOL_MINT : tradeData.tokenMint;
      const outputMint = tradeData.tradeType === 'buy' ? tradeData.tokenMint : SOL_MINT;
      calculateTradingFees(inputMint, outputMint, tradeData.amount);
    } else {
      setCalculatedAmount(null);
      setTradingFees(null);
    }
  }, [tradeData.tokenMint, tradeData.amount, tradeData.tradeType, calculateAmount, calculateTradingFees]);

  // Fetch wallet balance when wallet or token changes
  useEffect(() => {
    fetchWalletBalance();
  }, [fetchWalletBalance]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tokenFetchTimeoutRef.current) {
        clearTimeout(tokenFetchTimeoutRef.current);
      }
    };
  }, []);

  const getQuote = async () => {
    if (!tradeData.tokenMint || !tradeData.amount || !activeWallet) {
      showError('Please fill in all fields and connect wallet');
      return;
    }

    try {
      setLoading(true);
      
      const SOL_MINT = 'So11111111111111111111111111111111111111112';
      const inputMint = tradeData.tradeType === 'buy' ? SOL_MINT : tradeData.tokenMint;
      const outputMint = tradeData.tradeType === 'buy' ? tradeData.tokenMint : SOL_MINT;
      const amountLamports = tradeData.tradeType === 'buy' 
        ? Math.floor(parseFloat(tradeData.amount) * 1e9)
        : Math.floor(parseFloat(tradeData.amount) * 1e6);

      const data = await makeAuthenticatedRequest('/trading/quote', {
        method: 'POST',
        body: JSON.stringify({
          inputMint,
          outputMint,
          amount: amountLamports,
          slippageBps: Math.floor(tradeData.slippage * 100)
        })
      });

      setQuote(data.data.quote);
      setStep('quote');
    } catch (error) {
      showError(`Failed to get quote: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const executeTrade = async () => {
    if (!quote || !activeWallet) {
      showError('Please get a quote first');
      return;
    }

    try {
      setLoading(true);
      setStep('execute');

      const endpoint = tradeData.tradeType === 'buy' ? '/trading/manual-buy' : '/trading/manual-sell';
      const payload = tradeData.tradeType === 'buy' 
        ? {
            tokenMint: tradeData.tokenMint,
            solAmount: parseFloat(tradeData.amount),
            walletId: activeWallet.id
          }
        : {
            tokenMint: tradeData.tokenMint,
            tokenAmount: parseFloat(tradeData.amount),
            walletId: activeWallet.id
          };

      const data = await makeAuthenticatedRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      // Reset form
      setTradeData({ ...tradeData, amount: '' });
      setQuote(null);
      setStep('input');
      
      if (onTradeComplete) onTradeComplete();
      
      showTransaction(
        tradeData.tradeType, 
        tradeData.tokenMint, 
        tradeData.amount, 
        data.data.transaction.signature
      );
    } catch (error) {
      showError(`Failed to execute trade: ${error.message}`);
      setStep('quote');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep('input');
    setQuote(null);
    setTradeData({ ...tradeData, amount: '' });
  };

  const formatAmount = (amount, decimals = 6) => {
    return (amount / Math.pow(10, decimals)).toFixed(decimals);
  };

  // Show login prompt if not authenticated
  if (!tokens?.access_token) {
    return (
      <div className="compact-manual-trading">
        <div className="auth-required">
          <div className="auth-icon">🔐</div>
          <h3>Authentication Required</h3>
          <p>Please log in to use the trading features.</p>
          <div className="auth-actions">
            <button className="login-btn" onClick={() => window.location.href = '/login'}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="compact-manual-trading">
      {step === 'input' && (
        <div className="trade-input-form">
          {/* Trade Type Toggle */}
          <div className="trade-type-toggle">
            <button
              className={`toggle-btn ${tradeData.tradeType === 'buy' ? 'active buy' : ''}`}
              onClick={() => setTradeData({ ...tradeData, tradeType: 'buy' })}
            >
              Buy
            </button>
            <button
              className={`toggle-btn ${tradeData.tradeType === 'sell' ? 'active sell' : ''}`}
              onClick={() => setTradeData({ ...tradeData, tradeType: 'sell' })}
            >
              Sell
            </button>
          </div>

          {/* Token Input */}
          <div className="input-group">
            <label className="input-label">Token Address</label>
            <div className="token-input-wrapper">
              <input
                type="text"
                className="compact-input"
                value={tradeData.tokenMint}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setTradeData({ ...tradeData, tokenMint: newValue });
                  // Trigger debounced fetch immediately on change
                  if (newValue) {
                    debouncedFetchTokenInfo(newValue);
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault(); // Prevent default paste behavior
                  const pastedValue = e.clipboardData.getData('text').trim();
                  if (pastedValue) {
                    setTradeData({ ...tradeData, tokenMint: pastedValue });
                    // Trigger fetch immediately for paste
                    if (isValidSolanaAddress(pastedValue)) {
                      fetchTokenInfo(pastedValue);
                    }
                  }
                }}
                placeholder="Enter token mint address..."
                disabled={loading}
              />
              {tokenInfoLoading && (
                <div className="token-loading">
                  <div className="loading-spinner"></div>
                </div>
              )}
            </div>
            
            {/* Token Information Display */}
            {tokenInfo && (
              <div className="token-info-display">
                <div className="token-header">
                  <div className="token-identity">
                    {tokenInfo.logoURI ? (
                      <img 
                        src={tokenInfo.logoURI} 
                        alt={tokenInfo.symbol} 
                        className="token-logo"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    {!tokenInfo.logoURI && (
                      <div className="token-logo-placeholder">
                        {tokenInfo.symbol?.charAt(0) || '?'}
                      </div>
                    )}
                    <div className="token-names">
                      <span className="token-symbol">{tokenInfo.symbol}</span>
                      <span className="token-name">{tokenInfo.name}</span>
                    </div>
                  </div>
                  {tokenInfo.isVerified && (
                    <div className="verified-badge">✓</div>
                  )}
                </div>
                
                <div className="token-metrics">
                  <div className="metric">
                    <span className="metric-label">Price</span>
                    <span className="metric-value">
                      ${tokenInfo.priceUSD ? (tokenInfo.priceUSD < 0.001 ? tokenInfo.priceUSD.toExponential(3) : tokenInfo.priceUSD.toFixed(6)) : 'N/A'}
                    </span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Market Cap</span>
                    <span className="metric-value">
                      ${tokenInfo.marketCap ? (tokenInfo.marketCap / 1000000).toFixed(2) + 'M' : 'N/A'}
                    </span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">24h Change</span>
                    <span className={`metric-value ${tokenInfo.priceChange24h >= 0 ? 'positive' : 'negative'}`}>
                      {tokenInfo.priceChange24h?.toFixed(2) || '0.00'}%
                    </span>
                  </div>
                </div>
                
                <button className="add-to-assets-btn" onClick={addTokenToAssets}>
                  Add to Assets
                </button>
              </div>
            )}
            
            {tokenInfo?.error && (
              <div className="token-error">
                ⚠️ {tokenInfo.error}
              </div>
            )}
          </div>

          {/* Amount Input */}
          <div className="input-group">
            <label className="input-label">
              {tradeData.tradeType === 'buy' ? 'SOL Amount' : 'Token Amount'}
            </label>
            <div className="amount-input-wrapper">
              <input
                type="number"
                className="compact-input"
                value={tradeData.amount}
                onChange={(e) => setTradeData({ ...tradeData, amount: e.target.value })}
                placeholder={tradeData.tradeType === 'buy' ? '0.1' : '1000'}
                step="0.000001"
                disabled={loading}
              />
              <span className="amount-suffix">
                {tradeData.tradeType === 'buy' ? 'SOL' : 'TOKENS'}
              </span>
              {amountCalculating && (
                <div className="amount-calculating">
                  <div className="loading-spinner"></div>
                </div>
              )}
            </div>
            
            {/* Percentage Buttons */}
            <div className="percentage-buttons">
              <button 
                className="percentage-btn" 
                onClick={() => setPercentageAmount(25)}
                disabled={loading}
              >
                25%
              </button>
              <button 
                className="percentage-btn" 
                onClick={() => setPercentageAmount(50)}
                disabled={loading}
              >
                50%
              </button>
              <button 
                className="percentage-btn" 
                onClick={() => setPercentageAmount(75)}
                disabled={loading}
              >
                75%
              </button>
              <button 
                className="percentage-btn max" 
                onClick={() => setPercentageAmount(100)}
                disabled={loading}
              >
                MAX
              </button>
            </div>
            
            {/* Calculated Amount Display */}
            {calculatedAmount && (
              <div className="calculated-amount">
                <div className="calculation-result">
                  <span className="calculation-label">You will receive:</span>
                  <span className="calculation-value">
                    {tradeData.tradeType === 'buy' 
                      ? `${calculatedAmount.tokensReceived?.toFixed(6) || '0'} ${tokenInfo?.symbol || 'TOKENS'}`
                      : `${calculatedAmount.solReceived?.toFixed(6) || '0'} SOL`
                    }
                  </span>
                </div>
                <div className="calculation-details">
                  <span className="detail-item">
                    Rate: {calculatedAmount.exchangeRate?.toFixed(8) || '0'} 
                    {tradeData.tradeType === 'buy' ? ` ${tokenInfo?.symbol || 'TOKENS'}/SOL` : ' SOL/TOKEN'}
                  </span>
                  <span className="detail-item">
                    Impact: {calculatedAmount.priceImpact?.toFixed(2) || '0'}%
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Slippage */}
          <div className="slippage-group">
            <label className="input-label">Slippage</label>
            <div className="slippage-options">
              {[0.1, 0.5, 1.0, 2.0].map(value => (
                <button
                  key={value}
                  className={`slippage-btn ${tradeData.slippage === value ? 'active' : ''}`}
                  onClick={() => setTradeData({ ...tradeData, slippage: value })}
                >
                  {value}%
                </button>
              ))}
            </div>
          </div>

          {/* Trading Fees Display */}
          {tradingFees && (
            <div className="trading-fees">
              <label className="input-label" style={{ color: 'black' }}>
                Trading Fees
              </label>
              <div className="fees-breakdown">
                <div className="fee-item">
                  <span className="fee-label" style={{ color: 'brown' }}>Network Fee:</span>
                  <span className="fee-value" style={{ color: 'brown' }}>{tradingFees.networkFee?.toFixed(6) || '0'} SOL</span>
                </div>
                <div className="fee-item">
                  <span className="fee-label" style={{ color: 'brown' }}>Priority Fee:</span>
                  <span className="fee-value" style={{ color: 'brown' }}>{tradingFees.priorityFee?.toFixed(6) || '0'} SOL</span>
                </div>
                {tradingFees.routingFee > 0 && (
                  <div className="fee-item">
                    <span className="fee-label" style={{ color: 'brown' }}>Routing Fee:</span>
                    <span className="fee-value" style={{ color: 'brown' }}>{tradingFees.routingFee?.toFixed(6) || '0'} SOL</span>
                  </div>
                )}
                <div className="fee-item total">
                  <span className="fee-label" style={{ color: 'brown' }}>
                    Total Fees:
                  </span>
                  <span className="fee-value" style={{ color: 'brown' }}>{tradingFees.totalFee?.toFixed(6) || '0'} SOL</span>
                </div>
              </div>
            </div>
          )}

          {/* Get Quote Button */}
          <button
            className="quote-btn"
            onClick={getQuote}
            disabled={!tradeData.tokenMint || !tradeData.amount || !activeWallet || loading}
          >
            {loading ? (
              <>
                <div className="loading-spinner"></div>
                Getting Quote...
              </>
            ) : (
              'Get Quote'
            )}
          </button>
        </div>
      )}

      {step === 'quote' && quote && (
        <div className="quote-review">
          <div className="quote-header">
            <h4>Review Trade</h4>
            <button className="back-btn" onClick={resetForm}>←</button>
          </div>

          <div className="quote-details">
            <div className="trade-summary">
              <div className="summary-item">
                <span className="label">You {tradeData.tradeType}:</span>
                <span className="value">
                  {tradeData.tradeType === 'buy' 
                    ? `${formatAmount(quote.inputAmount, 9)} SOL`
                    : `${formatAmount(quote.inputAmount, 6)} Tokens`
                  }
                </span>
              </div>
              
              <div className="arrow-down">↓</div>
              
              <div className="summary-item">
                <span className="label">You receive:</span>
                <span className="value">
                  {tradeData.tradeType === 'buy' 
                    ? `${formatAmount(quote.outputAmount, 6)} Tokens`
                    : `${formatAmount(quote.outputAmount, 9)} SOL`
                  }
                </span>
              </div>
            </div>

            <div className="quote-metrics">
              <div className="metric-item">
                <span className="metric-label">Price Impact</span>
                <span className={`metric-value ${quote.priceImpact > 5 ? 'high' : ''}`}>
                  {quote.priceImpact?.toFixed(2) || '0.00'}%
                </span>
              </div>
              <div className="metric-item">
                <span className="metric-label">Slippage</span>
                <span className="metric-value">{tradeData.slippage}%</span>
              </div>
            </div>
          </div>

          <button
            className={`execute-btn ${tradeData.tradeType}`}
            onClick={executeTrade}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="loading-spinner"></div>
                Executing...
              </>
            ) : (
              `Execute ${tradeData.tradeType.toUpperCase()}`
            )}
          </button>
        </div>
      )}

      {step === 'execute' && (
        <div className="trade-executing">
          <div className="executing-animation">
            <div className="pulse-circle"></div>
            <div className="executing-icon">⚡</div>
          </div>
          <h4>Executing Trade...</h4>
          <p>Please wait while your transaction is processed</p>
        </div>
      )}

      {!activeWallet && (
        <div className="wallet-warning">
          <div className="warning-icon">⚠️</div>
          <p>Please connect a wallet to start trading</p>
        </div>
      )}
    </div>
  );
};

export default CompactManualTrading;